import {Box, Tab, Tabs, TabsProps} from '@mui/material';
import {usePersona} from 'src/permissions/hooks';
import {FC} from 'react';
import {useIntl} from 'react-intl';
import {useDefineIsRealEstateRole} from 'src/app/hooks';
import {ChargerListCard} from 'src/components/chargers/ChargerListCard';
import {columnConfigChargers} from 'src/components/chargers/ChargerTable/ChargerTable.tableConfig';
import {TabPanel} from 'src/components/common/TabPanel';
import {ContractItemCard} from 'src/components/contracts/ContractItemCard';
import {columnConfigContracts} from 'src/components/contracts/ContractsTable/ContractsTable.tableConfig';
import {LocationItemCard} from 'src/components/locations/LocationTable/LocationItemCard';
import {columnConfigLocations} from 'src/components/locations/LocationTable/LocationTable.tableConfig';
import {Table} from 'src/components/tables/Table';
import {TableLayout} from 'src/components/tables/TableLayout';
import {tabsBandProps, tabsBandSx, tabSx} from './BusinessTables.sx';
import {BusinessTablesType} from './BusinessTables.types';
import {ResponsiveTable} from '../../common/ResponsiveTable';
import {CardList} from 'src/components/common/CardList';

export const BusinessTables: FC<BusinessTablesType> = ({
                                                           locations,
                                                           chargers,
                                                           contracts,
                                                           handleChangePage,
                                                           handleChangeRowsNumber,
                                                           handleSetActiveTab,
                                                           tab,
                                                           page,
                                                           size,
                                                           locationsTotalPage,
                                                           chargersTotalPage,
                                                           contractsTotalPage,
                                                           handleSort,
                                                       }) => {
    const persona = usePersona();
    const handleSetTab: TabsProps['onChange'] = (_event, newValue) => {
        handleSetActiveTab(newValue);
    };

    const isRealEstate = useDefineIsRealEstateRole();

    const intl = useIntl();
    // Whichever tab is open. The three tabs shared one page and one page size and differed only in how
    // many pages there were, so the footer was written out three times to vary a single number.
    const totalPages = [locationsTotalPage, chargersTotalPage, contractsTotalPage][tab] ?? 0;

    return (
        <TableLayout
            toolbar={(
                // Inside the card, as a band — the same strip the relationships panels use. It was
                // above the card on a hairline, so the tabs looked like page navigation rather than
                // three views of one table.
                <Box sx={tabsBandSx}>
                    <Tabs
                        {...tabsBandProps}
                        value={tab}
                        onChange={handleSetTab}
                        aria-label="business tables"
                    >
                        <Tab
                            sx={tabSx}
                            label={intl.formatMessage({id: 'locations'})}
                        />
                        <Tab
                            sx={tabSx}
                            label={intl.formatMessage({id: 'chargers'})}
                        />
                        <Tab
                            sx={tabSx}
                            label={intl.formatMessage({id: 'contracts'})}
                        />
                    </Tabs>
                </Box>
            )}
            totalPages={totalPages}
            page={page}
            pageSize={size}
            onPageChange={handleChangePage}
            onPageSizeChange={handleChangeRowsNumber}
        >

            {/* LOCATIONS TAB */}
            <TabPanel
                value={tab}
                index={0}
            >
                <ResponsiveTable
                    responsiveElement={
                        <CardList>
                            {locations.map((item) => (
                                <LocationItemCard
                                    key={item?.id}
                                    {...item}
                                    canCreateDeleteLocation={!isRealEstate}
                                />
                            ))}
                        </CardList>
                    }
                    desktopElement={
                        <Table
                            handleSort={handleSort}
                            rows={locations}
                            hideFooter
                            columns={columnConfigLocations(!isRealEstate)}
                        />
                    }
                />
            </TabPanel>

            {/* CHARGERS TAB */}
            <TabPanel
                value={tab}
                index={1}
            >
                <ResponsiveTable
                    responsiveElement={
                        <CardList>
                            {chargers.map((item) => (
                                <ChargerListCard
                                    key={item?.id}
                                    {...item}
                                    canRemoveCharger={!isRealEstate}
                                />
                            ))}
                        </CardList>
                    }
                    desktopElement={
                        <Table
                            handleSort={handleSort}
                            rows={chargers}
                            hideFooter
                            columns={columnConfigChargers(persona.isPlatformAdmin, !isRealEstate)}
                        />
                    }
                />
            </TabPanel>

            {/* CONTRACTS TAB */}
            <TabPanel
                value={tab}
                index={2}
            >
                <ResponsiveTable
                    responsiveElement={
                        <CardList>
                            {contracts.map((item) => (
                                <ContractItemCard
                                    key={item?.id}
                                    {...item}
                                />
                            ))}
                        </CardList>
                    }
                    desktopElement={
                        <Table
                            handleSort={handleSort}
                            rows={contracts}
                            hideFooter
                            columns={columnConfigContracts(persona.isPlatformAdmin, persona.isCompany)}
                        />
                    }
                />
            </TabPanel>
        </TableLayout>
    );
};
